// Full-document render of the pillows ProductFilterGridPage: PageSkeleton (the
// React document shell) wrapping the category-list + hero markup, then the
// hero-sidekick, top-banner, and page-body islands in the same order as the
// legacy tempur.html layout block.
import { Immutable } from "immer";
import React from "react";
import { BannerBlock } from "tsi-common-react/src/components/BannerBlock/index";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { SidekickBlock } from "../blocks/SidekickBlock";
import { PageSkeleton } from "../components/PageSkeleton";
import { ProductFilterGridPageBody } from "../components/ProductFilterGridPageBody";

/**
 * Pillows ProductFilterGridPage hero-sidekick assembly: dispatches the
 * ``ProductFilterGridHeroSidekickBlock`` StreamField to its React component,
 * replacing the legacy ``{% for block in self.hero_sidekick %}
 * {% include_block block %}{% endfor %}`` hydration roots.
 */
const ProductFilterGridHeroSidekick = (
    props: Immutable<{ page: templates.ProductFilterGridPageTemplate["page"] }>,
): React.JSX.Element | null => {
    if (!props.page.hero_sidekick) {
        return null;
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.hero_sidekick.value}
            components={{
                hero_sidekick: SidekickBlock,
            }}
        />
    );
};

/**
 * Pillows ProductFilterGridPage top-banner assembly: dispatches the
 * ``ProductFilterGridTopBannerBlock`` StreamField to the shared
 * ``BannerBlock`` component, replacing the legacy
 * ``{% for block in self.top_banner %}{% include_block block %}{% endfor %}``
 * hydration roots.
 */
const ProductFilterGridTopBanner = (
    props: Immutable<{ page: templates.ProductFilterGridPageTemplate["page"] }>,
): React.JSX.Element | null => {
    if (!props.page.top_banner) {
        return null;
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.top_banner.value}
            components={{
                banner: BannerBlock,
            }}
        />
    );
};

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
export const Template = (
    props: templates.ProductFilterGridPageTemplate,
): React.JSX.Element => {
    const {
        page,
        hero_image_url,
        hero_title_html,
        hero_title_mobile_html,
        hero_link_url,
        shop_all_link_url,
        hero_badge_image_url,
        hero_badge_image_alt,
        hero_badge_position,
    } = props;

    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="l-full-width">
                <div className="category-list category-list--pillows u-full-width-title"></div>
                <div
                    className="hero product-list-hero product-list-hero--pillows l-full-width"
                    style={
                        hero_image_url
                            ? { backgroundImage: `url(${hero_image_url})` }
                            : undefined
                    }
                >
                    <div className="l-capped-width product-list-hero__filter-grid-hero-wrapper">
                        {hero_badge_image_url && (
                            <div className="product-list-hero-badge__image-container product-list-hero-badge__image-container--pillows">
                                <div
                                    className={`product-list-hero-badge__image-container-inner${
                                        hero_badge_position
                                            ? ` product-list-hero-badge__image-container-inner--${hero_badge_position}`
                                            : ""
                                    }`}
                                >
                                    <img
                                        className="responsive-img"
                                        src={hero_badge_image_url}
                                        alt={hero_badge_image_alt}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="product-list-hero__inner">
                            <div className="product-list-hero__content product-list-hero__content--pillows">
                                {page.hero_pre_title && (
                                    <h4 className="product-list-hero__category product-list-hero__category--pillows">
                                        {page.hero_pre_title}
                                    </h4>
                                )}
                                <h1 className="product-list-hero__title product-list-hero__title--pillows">
                                    <div
                                        className="rich-text"
                                        dangerouslySetInnerHTML={{
                                            __html: hero_title_html,
                                        }}
                                    />
                                </h1>
                                {page.hero_description && (
                                    <p className="product-list-hero__description product-list-hero__description--pillows">
                                        {page.hero_description}
                                    </p>
                                )}
                                {page.hero_link_copy && (
                                    <div className="product-list-hero__button">
                                        <a
                                            href={hero_link_url}
                                            className="button"
                                            role="button"
                                        >
                                            {page.hero_link_copy}
                                        </a>
                                    </div>
                                )}
                                {page.shop_all_link_copy && (
                                    <a href={shop_all_link_url}>
                                        <span className="product-list-hero__shop-link">
                                            {page.shop_all_link_copy}
                                        </span>
                                    </a>
                                )}
                            </div>

                            <div className="product-list-hero__content product-list-hero__content--pillows product-list-hero__content--pillows-mobile">
                                {(page.hero_pre_title ||
                                    page.hero_pre_title_mobile) && (
                                    <h4 className="product-list-hero__category product-list-hero__category--pillows">
                                        {page.hero_pre_title_mobile
                                            ? page.hero_pre_title_mobile
                                            : page.hero_pre_title}
                                    </h4>
                                )}
                                <h1 className="product-list-hero__title product-list-hero__title--pillows">
                                    <div
                                        className="rich-text"
                                        dangerouslySetInnerHTML={{
                                            __html: hero_title_mobile_html
                                                ? hero_title_mobile_html
                                                : hero_title_html,
                                        }}
                                    />
                                </h1>
                                {(page.hero_description_mobile ||
                                    page.hero_description) && (
                                    <p className="product-list-hero__description product-list-hero__description--pillows">
                                        {page.hero_description_mobile
                                            ? page.hero_description_mobile
                                            : page.hero_description}
                                    </p>
                                )}
                                {page.hero_link_copy && (
                                    <div className="product-list-hero__button">
                                        <a
                                            href={hero_link_url}
                                            className="button"
                                            role="button"
                                        >
                                            {page.hero_link_copy}
                                        </a>
                                    </div>
                                )}
                                {page.shop_all_link_copy && (
                                    <a href={shop_all_link_url}>
                                        <span className="product-list-hero__shop-link">
                                            {page.shop_all_link_copy}
                                        </span>
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <ProductFilterGridHeroSidekick page={page} />

                <ProductFilterGridTopBanner page={page} />

                {page.category_title && (
                    <div className="category-list-header pillows-index-header l-capped-width">
                        <div className="category-list-header__copy pillows-index-header__copy">
                            <div className="category-list-header__title">
                                {page.category_title}
                            </div>
                            <div className="category-list-header__sub-title">
                                {page.category_sub_title}
                            </div>
                        </div>
                    </div>
                )}

                <ProductFilterGridPageBody page={page} />
            </div>
        </PageSkeleton>
    );
};
